import {
  Component,
  effect,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { AvatarModule } from 'primeng/avatar';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { Bookmark } from '../../../models/bookmark';
import { GlobalsStore } from '../../../stores/globals.state';
import { getState } from '@ngrx/signals';
import { Button } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { UserFormComponent } from '../../organisms/user-form/user-form.component';
import { getProfileImagePath } from '../../../utility/app.utility';
import { User } from '../../../models/user';
import { HeaderComponent } from '../header/header.component';

@Component({
  selector: 'app-active-user-avatar',
  standalone: true,
  imports: [
    AvatarModule,
    MenuModule,
    Button,
    DialogModule,
    UserFormComponent,
    HeaderComponent,
  ],
  templateUrl: './active-user-avatar.component.html',
  styleUrl: './active-user-avatar.component.scss',
})
export class ActiveUserAvatarComponent implements OnInit, OnChanges {
  @Input() size: 'normal' | 'large' | 'xlarge' = 'normal';
  @Input() styleClass = '';
  @Input() icon = '';
  @Input() addBookmarks = false;
  /** Extra menu items (e.g. Info, Login, Kontakt) appended below the built-in ones. */
  @Input() extraItems: MenuItem[] = [];

  readonly activeUser = inject(ActiveUserStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly globalsStore = inject(GlobalsStore);

  menuItems: MenuItem[] = [];
  showProfileForm = false;

  /** URL of the active user's uploaded profile picture, or null if they haven't set one. */
  get profileImagePath(): string | null {
    const image = this.activeUser.user()?.image;
    if (!image) return null;
    return getProfileImagePath(this.activeUser.commune(), image);
  }

  /**
   * Closes the profile dialog and merges the saved values into `ActiveUserStore`
   * so the avatar/initials reflect the change immediately (the underlying
   * `UserStore.update()` only persists to the backend, it doesn't touch this store).
   */
  onProfileSaved(value: Partial<User> | undefined): void {
    this.showProfileForm = false;
    if (value) {
      this.activeUser.updateOwnProfile(value);
    }
  }

  constructor() {
    effect(() => {
      const bookmarkState = getState(this.bookmarkStore);

      if (
        this.addBookmarks &&
        bookmarkState.trigger &&
        bookmarkState.trigger.includes('bookmarkAdded')
      ) {
        queueMicrotask(() => {
          this.rebuildMenuItems();
        });
      }
    });
  }

  ngOnInit(): void {
    this.rebuildMenuItems();
  }

  /**
   * Rebuilds `menuItems` whenever `extraItems` changes - the parent typically
   * passes an empty array on first render and populates it asynchronously
   * shortly after, so this can't just run once in `ngOnInit()`.
   *
   * @param {SimpleChanges} changes - The changed input properties.
   * @return {void} Does not return a value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['extraItems'] && !changes['extraItems'].firstChange) {
      this.rebuildMenuItems();
    }
  }

  /**
   * Builds the menu items based on the state of the `addBookmarks` property.
   * If `addBookmarks` is true, it creates a structured menu including logout and
   * private views. Otherwise, it creates a minimal menu with only a logout option.
   * Either way, `extraItems` (e.g. Info, Login, Kontakt) is appended at the end.
   *
   * @return {void} This method does not return a value.
   */
  private rebuildMenuItems(): void {
    if (!this.activeUser.isLoggedIn()) {
      // guests have no logout/private views - just whatever was passed in (Login, Kontakt, ...).
      // All flat (no nested `.items`), so PrimeNG renders them as regular clickable entries.
      this.menuItems = [...this.extraItems];
      return;
    }

    // PrimeNG's `p-menu` renders EVERY top-level item as a (non-interactive)
    // group header the moment ANY top-level item has nested `.items` (e.g. the
    // "Nutzer:in" group below) - so flat extra items can't be spread in directly
    // here, they'd silently lose their click handling. Wrapping them in their
    // own group keeps every top-level entry uniformly grouped.
    const extraGroup: MenuItem[] =
      this.extraItems.length > 0
        ? [
            {
              label: 'Mehr',
              icon: 'fas fa-external-link-alt',
              items: this.extraItems,
            },
          ]
        : [];

    if (this.addBookmarks) {
      const privateBookmarks = this.bookmarkStore.private();

      this.menuItems = [
        this.createUserMenu(),
        this.createSeparator(),
        ...(privateBookmarks.length > 0
          ? [
              this.createViewMenu(
                'private Ansichten',
                'fas fa-eye-slash',
                privateBookmarks
              ),
            ]
          : []),
        ...extraGroup,
      ];
    } else {
      this.menuItems = [this.createUserMenu(), ...extraGroup];
    }
  }

  /**
   * Creates and returns a user menu configuration object.
   *
   * @return {MenuItem} The menu item object containing details about the user menu,
   * including label, icon, and associated sub-menu items.
   */
  private createUserMenu(): MenuItem {
    return {
      label: 'Nutzer:in',
      icon: 'fas fa-user-circle',
      items: [
        {
          label: 'Profil bearbeiten',
          sub: 'Eigene Nutzerdaten anpassen',
          icon: 'fas fa-id-badge',
          command: () => {
            this.showProfileForm = true;
          },
        },
        ...this.createLogoutMenu(),
      ],
    };
  }

  private createPrivateViewsMenu(bookmarks: Bookmark[]): MenuItem[] {
    return bookmarks.length > 0
      ? [
          this.createViewMenu(
            'private Ansichten',
            'fas fa-eye-slash',
            bookmarks
          ),
        ]
      : [];
  }

  private createUnsortedViewsMenu(bookmarks: Bookmark[]): MenuItem[] {
    return bookmarks.length > 0
      ? [
          this.createViewMenu(
            'unsortierte Ansichten',
            'fas fa-note-sticky',
            bookmarks
          ),
        ]
      : [];
  }

  /**
   * Creates and returns a menu item for logging out.
   *
   * @return {MenuItem} A menu item object that includes a label, an icon, and a command
   * to trigger the logout action with a redirect.
   */
  private createLogoutMenu(): MenuItem[] {
    const menu: MenuItem[] = [
      {
        label: 'Ausloggen',
        sub: 'Sitzung beenden und abmelden',
        icon: 'fa fa-right-from-bracket',
        command: () => {
          this.activeUser.logout({ redirect: true });
        },
      },
    ];

    const impersonatedUserId = this.activeUser.impersonatedBy();
    if (impersonatedUserId) {
      menu.push({
        label: 'Zurückwechseln',
        sub: 'Zurück zum ursprünglichen Nutzerkonto wechseln',
        icon: 'fas fa-people-arrows',
        command: () => {
          this.activeUser.switchBack(impersonatedUserId);
        },
      });
    }
    return menu;
  }

  /**
   * Creates a menu item with the specified label, icon, and bookmarks.
   *
   * @param {string} label - The label for the menu item.
   * @param {string} icon - The icon associated with the menu item.
   * @param {Bookmark[]} bookmarks - An array of bookmarks to be included in the menu item.
   * @return {MenuItem} The created menu item containing the label, icon, and transformed bookmarks.
   */
  private createViewMenu(
    label: string,
    icon: string,
    bookmarks: Bookmark[]
  ): MenuItem {
    return {
      label,
      icon,
      items: bookmarks.map(this.getMenuItemFromBookmark),
    };
  }

  /**
   * Creates a separator menu item with optional visibility.
   *
   * @param {boolean} [visible] - Determines if the separator should be visible. Defaults to true if not provided.
   * @return {MenuItem} The menu item object representing the separator.
   */
  private createSeparator(visible?: boolean): MenuItem {
    return { separator: visible ?? true };
  }

  /**
   * Converts a Bookmark object into a MenuItem object.
   *
   * @param {Bookmark} bookmark - The bookmark object containing metadata to create a menu item.
   * @return {MenuItem} The constructed menu item object containing label and routerLink properties.
   */
  private getMenuItemFromBookmark(bookmark: Bookmark): MenuItem {
    return {
      label: bookmark.title,
      routerLink: ['ansicht', bookmark._id],
    };
  }
}
